import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

const moments = [
  {
    number: "01",
    headline: "The day begins already prepared.",
    body: "Your liturgical calendar, tradition-specific readings, and a personal intention are waiting when you open Eden Pages. You're not starting from nothing.",
  },
  {
    number: "02",
    headline: "Write what's real. Receive what you need.",
    body: "Your companion reads what you wrote and reflects it back through scripture — not a generic verse, but one chosen for the words you used and the things you named.",
  },
  {
    number: "03",
    headline: "Every session ends with a prayer written from your own words.",
    body: "Not a template. Not a rotation. A prayer shaped from your entry — what you struggled with, what you named, what you couldn't quite say.",
  },
];

export default function ExperienceSection() {
  return (
    <section id="how-it-works" className="py-[120px] px-6 sm:px-10 overflow-hidden" style={{ background: C.cream }}>
      <div className="max-w-[860px] mx-auto">

        {/* Section heading */}
        <FadeIn className="text-center mb-[80px]">
          <h2
            className="font-heading font-semibold text-spec-brown mx-auto"
            style={{ fontSize: "clamp(36px, 4.5vw, 58px)", lineHeight: 1.1, maxWidth: 560 }}
          >
            What your mornings
            <br />
            <em className="italic text-spec-sage">could actually look like.</em>
          </h2>
        </FadeIn>

        {/* Three numbered moments */}
        <div className="max-w-[580px] mx-auto">
          {moments.map((m, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div
                className="flex gap-8 sm:gap-12 items-start"
                style={{
                  paddingBottom: 56,
                  marginBottom: i < moments.length - 1 ? 0 : 0,
                  borderBottom: i < moments.length - 1 ? `1px solid ${C.bdr}` : "none",
                  paddingTop: i > 0 ? 56 : 0,
                }}
              >
                {/* Number */}
                <div className="flex-shrink-0 pt-1">
                  <span
                    className="font-heading font-semibold"
                    style={{ fontSize: 13, color: C.sageMuted, letterSpacing: "1px" }}
                  >
                    {m.number}
                  </span>
                </div>

                {/* Content */}
                <div>
                  <h3
                    className="font-heading font-semibold text-spec-brown mb-3"
                    style={{ fontSize: "clamp(20px, 2.5vw, 26px)", lineHeight: 1.25 }}
                  >
                    {m.headline}
                  </h3>
                  <p className="font-ui text-[15px] text-spec-txt2 leading-[1.75] m-0">
                    {m.body}
                  </p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Journal extract — the actual content, set as typography */}
        <FadeIn delay={0.3} className="mt-20">
          <div
            className="mx-auto"
            style={{
              maxWidth: 580,
              padding: "clamp(32px, 5vw, 52px) clamp(28px, 5vw, 48px)",
              background: C.white,
              border: `1px solid ${C.bdr}`,
              boxShadow: "0 8px 48px rgba(61,50,37,0.08), 0 2px 12px rgba(61,50,37,0.04)",
            }}
          >
            <p
              className="font-ui mb-8"
              style={{ fontSize: 11, color: C.txt3, letterSpacing: "1.4px" }}
            >
              FROM A SESSION
            </p>

            {/* The journal entry */}
            <p
              className="font-heading italic text-spec-brown mb-8"
              style={{ fontSize: "clamp(17px, 2vw, 21px)", lineHeight: 1.9 }}
            >
              "The woman at the well keeps me. That moment when Jesus knows
              everything about her and still asks for water — still chooses to
              need something from her. I want that kind of being-known."
            </p>

            {/* Companion response */}
            <div
              className="mb-8 pl-5"
              style={{ borderLeft: `2px solid ${C.sageBdr}` }}
            >
              <p
                className="font-ui mb-2"
                style={{ fontSize: 11, color: C.sage, letterSpacing: "1.2px" }}
              >
                PASTOR · REFLECTION
              </p>
              <p className="font-ui text-[14px] text-spec-txt2 leading-[1.75] m-0">
                You named the difference between being known and being exposed.
                The woman at the well met the first. That distinction is worth
                staying with today.
              </p>
            </div>

            {/* Closing prayer */}
            <div className="pt-6" style={{ borderTop: `1px solid ${C.bdr}` }}>
              <p
                className="font-ui mb-2"
                style={{ fontSize: 11, color: C.gold, letterSpacing: "1.2px" }}
              >
                CLOSING PRAYER
              </p>
              <p
                className="font-heading italic m-0"
                style={{
                  fontSize: "clamp(15px, 1.6vw, 17px)",
                  color: C.txt2,
                  lineHeight: 1.85,
                }}
              >
                "Lord, I brought what I could not name and you met me there.
                Teach me that being fully known by you is not exposure — it is
                grace. Let that be enough today. Amen."
              </p>
            </div>
          </div>
        </FadeIn>

      </div>
    </section>
  );
}
